import { useEffect, useReducer, useRef } from "react";
import { logConnectorFailure } from "../../../telemetry";

/** Something to read once per session: its cache key, the id its value is returned under, and how to read it. */
export interface LoadOnce<T> {
  readonly key: string;
  readonly id: string;
  readonly load: () => Promise<T | null>;
}

/** Reads each item once into `cache` (null while loading, unreadable or empty); returns the values read so far, by id. */
export function useLoadedOnce<T>(cache: Map<string, T | null>, items: readonly LoadOnce<T>[], failure: string): ReadonlyMap<string, T> {
  // The values live in `cache`; this only re-renders once one has landed.
  const [, loaded] = useReducer((n: number) => n + 1, 0);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const keys = items.map((i) => i.key).join("\n");

  useEffect(() => {
    for (const { key, load } of itemsRef.current) {
      if (cache.has(key)) continue;
      cache.set(key, null);
      load()
        .then((value) => {
          cache.set(key, value);
          loaded();
        })
        .catch((e: unknown) => logConnectorFailure("calendar", e, failure));
    }
  }, [keys, cache, failure]);

  const out = new Map<string, T>();
  for (const { key, id } of items) {
    const value = cache.get(key);
    if (value !== undefined && value !== null) out.set(id, value);
  }
  return out;
}
